import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";

const SHIFT_KEYS = ["1", "2", "3"];
const SHIFT_LABELS = { 1: "I", 2: "II", 3: "III" };

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString("id-ID", { maximumFractionDigits: 2 });
};

const text = (value) => (value === null || value === undefined ? "" : String(value));

const parseMaterialMasuk = (raw) => {
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw || "[]") : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatDateId = (tanggal) => {
  if (!tanggal) return "";
  const date = new Date(`${tanggal}T00:00:00`);
  if (Number.isNaN(date.getTime())) return tanggal;
  return date.toLocaleDateString("id-ID", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const getShift = (data, shift) => data?.shifts?.[shift] || { main: null, castings: [], pengirimans: [], problems: [], infos: [] };

const getMain = (data, shift) => getShift(data, shift).main || {};

const combineProductionRows = (data, key, minRows = 10) => {
  const grouped = new Map();
  SHIFT_KEYS.forEach((shift) => {
    const rows = getShift(data, shift)[key] || [];
    rows.forEach((row, index) => {
      const lot = text(row.no_lot).trim();
      const mapKey = lot || `__blank_${shift}_${index}`;
      if (!grouped.has(mapKey)) {
        grouped.set(mapKey, { no_lot: lot, values: { 1: "", 2: "", 3: "" } });
      }
      const groupedRow = grouped.get(mapKey);
      groupedRow.values[shift] = toNumber(groupedRow.values[shift]) + toNumber(row.total);
    });
  });

  const rows = Array.from(grouped.values()).map((row) => ({
    ...row,
    total: SHIFT_KEYS.reduce((sum, shift) => sum + toNumber(row.values[shift]), 0),
  }));

  while (rows.length < minRows) {
    rows.push({ no_lot: "", values: { 1: "", 2: "", 3: "" }, total: "" });
  }
  return rows;
};

const getProductionExtraRows = (data, key, minRows) => (
  Math.max(0, combineProductionRows(data, key, minRows).length - minRows)
);

const totalByShift = (data, key, shift) => {
  const rows = getShift(data, shift)[key] || [];
  return rows.reduce((sum, row) => sum + toNumber(row.total), 0);
};

const sumMain = (data, field) => SHIFT_KEYS.reduce((sum, shift) => sum + toNumber(getMain(data, shift)[field]), 0);

function MiniCell({ children, className = "" }) {
  return <td className={className}>{children}</td>;
}

function ProductionBlock({ title, rows, type, data }) {
  const minRows = type === "casting" ? 10 : 9;
  const rowCount = Math.max(minRows, rows.length);
  const padded = rows.slice();
  while (padded.length < rowCount) padded.push({ no_lot: "", values: { 1: "", 2: "", 3: "" }, total: "" });

  return (
    <>
      {padded.map((row, index) => (
        <tr key={`${type}-${index}`}>
          {index === 0 && <td rowSpan={rowCount + 1} className="vertical-label">{title}</td>}
          <td colSpan={2} className="lot-cell">{row.no_lot}</td>
          <td className="num-cell">{fmt(row.values[1])}</td>
          <td className="num-cell">{fmt(row.values[2])}</td>
          <td className="num-cell">{fmt(row.values[3])}</td>
          <td className="num-cell">{row.total === "" ? "" : fmt(row.total)}</td>
        </tr>
      ))}
      <tr className="total-row">
        <td colSpan={2}>TOTAL</td>
        <td className="num-cell">{fmt(totalByShift(data, type === "casting" ? "castings" : "pengirimans", "1"))}</td>
        <td className="num-cell">{fmt(totalByShift(data, type === "casting" ? "castings" : "pengirimans", "2"))}</td>
        <td className="num-cell">{fmt(totalByShift(data, type === "casting" ? "castings" : "pengirimans", "3"))}</td>
        <td className="num-cell">{fmt(totalByShift(data, type === "casting" ? "castings" : "pengirimans", "1") + totalByShift(data, type === "casting" ? "castings" : "pengirimans", "2") + totalByShift(data, type === "casting" ? "castings" : "pengirimans", "3"))}</td>
      </tr>
    </>
  );
}

function MetricRow({ label, field, data, subLabel = "" }) {
  const values = SHIFT_KEYS.map((shift) => getMain(data, shift)[field]);
  return (
    <tr>
      <td colSpan={subLabel ? 2 : 3} className="left-label">{label}</td>
      {subLabel && <td className="left-sub-label">{subLabel}</td>}
      {SHIFT_KEYS.map((shift, index) => <td key={shift} className="num-cell">{fmt(values[index])}</td>)}
      <td className="num-cell">{fmt(values.reduce((sum, value) => sum + toNumber(value), 0))}</td>
    </tr>
  );
}

function LeftSummaryTable({ data }) {
  const castings = useMemo(() => combineProductionRows(data, "castings", 10), [data]);
  const pengirimans = useMemo(() => combineProductionRows(data, "pengirimans", 9), [data]);

  return (
    <table className="print-table left-table">
      <thead>
        <tr>
          <th className="item-col">ITEM</th>
          <th colSpan={2}>No. Lot</th>
          <th>I</th>
          <th>II</th>
          <th>III</th>
          <th>TOTAL</th>
        </tr>
      </thead>
      <tbody>
        <ProductionBlock title="CASTING" rows={castings} type="casting" data={data} />
        <ProductionBlock title="PENGIRIMAN AA" rows={pengirimans} type="pengiriman" data={data} />
        <tr className="total-row">
          <td colSpan={3}>NSP</td>
          <td colSpan={4}></td>
        </tr>
        <tr className="total-row">
          <td colSpan={3}>GRAND TOTAL</td>
          <td className="num-cell">{fmt(totalByShift(data, "castings", "1") + totalByShift(data, "pengirimans", "1"))}</td>
          <td className="num-cell">{fmt(totalByShift(data, "castings", "2") + totalByShift(data, "pengirimans", "2"))}</td>
          <td className="num-cell">{fmt(totalByShift(data, "castings", "3") + totalByShift(data, "pengirimans", "3"))}</td>
          <td className="num-cell">{fmt(SHIFT_KEYS.reduce((sum, shift) => sum + totalByShift(data, "castings", shift) + totalByShift(data, "pengirimans", shift), 0))}</td>
        </tr>
        <MetricRow label="BUTT YANG DITERIMA" field="butt_diterima" data={data} />
        <MetricRow label="Rata - Rata Butt" subLabel="Berat" field="berat_rata_rata" data={data} />
        <MetricRow label="" subLabel="Tinggi" field="tinggi_rata_rata" data={data} />
        <MetricRow label="TOTAL BUTT TEBAL" field="butt_tebal" data={data} />
        <MetricRow label="TOTAL BUTT SILUMAN" field="butt_siluman" data={data} />
        <MetricRow label="Total Reject Sticking Kecil" field="sticking_kecil" data={data} />
        <MetricRow label="Total Reject Sticking Besar" field="sticking_besar" data={data} />
        <tr>
          <td rowSpan={3} colSpan={2} className="left-label center">SH-401</td>
          <td className="left-sub-label">Impeller 1</td>
          {SHIFT_KEYS.map((shift) => <td key={shift} className="num-cell">{fmt(getMain(data, shift).impeller_1)}</td>)}
          <td className="num-cell">{fmt(sumMain(data, "impeller_1"))}</td>
        </tr>
        <tr>
          <td className="left-sub-label">Impeller 2</td>
          {SHIFT_KEYS.map((shift) => <td key={shift} className="num-cell">{fmt(getMain(data, shift).impeller_2)}</td>)}
          <td className="num-cell">{fmt(sumMain(data, "impeller_2"))}</td>
        </tr>
        <tr>
          <td className="left-sub-label">Impeller 3</td>
          {SHIFT_KEYS.map((shift) => <td key={shift} className="num-cell">{fmt(getMain(data, shift).impeller_3)}</td>)}
          <td className="num-cell">{fmt(sumMain(data, "impeller_3"))}</td>
        </tr>
        <tr className="section-row"><td colSpan={7}>PERSONIL</td></tr>
        <tr className="total-row">
          <td colSpan={2}>Shift I</td>
          <td colSpan={2}>Shift II</td>
          <td colSpan={3}>Shift III</td>
        </tr>
        {Array.from({ length: 8 }).map((_, index) => (
          <tr key={`personil-${index}`}>
            <td colSpan={2}></td>
            <td colSpan={2}></td>
            <td colSpan={3}></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoLine({ label, value, unit = "", rightLabel = "", rightValue = "", rightUnit = "" }) {
  return (
    <div className="info-line">
      <span>{label}</span>
      <strong>{fmt(value)}</strong>
      <em>{unit}</em>
      {rightLabel && (
        <>
          <span>{rightLabel}</span>
          <strong>{fmt(rightValue)}</strong>
          <em>{rightUnit}</em>
        </>
      )}
    </div>
  );
}

function ShiftInformation({ data, shift }) {
  const main = getMain(data, shift);
  const materialMasuk = parseMaterialMasuk(main.pengiriman_material_masuk_json);
  return (
    <div className="info-box">
      <InfoLine label="ATC :" value={main.atc_aktif} />
      <InfoLine label="Forklift :" value={main.forklift_aktif} rightLabel="Sweeper :" rightValue={main.sweeper_aktif} />
      <InfoLine label="Dongkrak :" value="" rightLabel="Ban ATC :" rightValue="" />
      <InfoLine label="Impact :" value="" rightLabel="E/O :" rightValue={main.ce} />
      <InfoLine label="Kunci Shock :" value="" rightLabel="CE IF-401 :" rightValue={main.ce} />
      <InfoLine label="HT :" value={main.ht} rightLabel="" rightValue="" unit="Unit" />
      <InfoLine label="Stock Metal, AC-401 :" value={main.ac401} unit="%" />
      <InfoLine label="HF-401 :" value={main.hf401} unit="Kg" />
      <InfoLine label="Masuk Metal dari SCA :" value={main.masuk_metal_sca} unit="Kg" />
      <InfoLine label="HP-401 Rand Crust :" value={main.rand_crust} unit="Kg" />
      <InfoLine label="Ladle Dross :" value={main.ladle_dross} unit="Kg" />
      {shift === "2" && (
        <>
          <div className="info-subtitle">Pengiriman Material</div>
          <InfoLine label="Crust over 30 mm :" value={main.pengiriman_crust_over_30} unit="Kg" />
          <InfoLine label="Aluminium ball :" value={main.pengiriman_aluminium_ball} unit="Kg" />
          <InfoLine label="Metal Scrap :" value={main.pengiriman_metal_scrap} unit="Kg" />
          <InfoLine label="Dust Hammer Mill :" value={main.pengiriman_dust_hammer_mill} unit="Kg" />
          <InfoLine label="Aluminium scrap :" value={main.pengiriman_aluminium_scrap} unit="Kg" />
          {materialMasuk.length === 0 ? (
            <InfoLine label="Material Masuk :" value="" unit="Kg" />
          ) : (
            materialMasuk.slice(0, 3).map((item, index) => (
              <InfoLine key={index} label={`${item.name || "Material Masuk"} :`} value={item.kg} unit="Kg" />
            ))
          )}
        </>
      )}
      <InfoLine label="Kadar GC 401 :" value={main.kadar_gc401} unit="%" />
      {shift === "3" && <InfoLine label="Data LNG (23:30) :" value={main.lng_2330} unit="Nm3" />}
      <InfoLine label="Sisa Butt :" value={main.sisa_butt} unit="Pcs" />
    </div>
  );
}

function ProblemRows({ data, shift, baseRows, extraRows = 0 }) {
  const shiftData = getShift(data, shift);
  const problems = shiftData.problems || [];
  const rows = Array.from({ length: Math.max(baseRows, problems.length || 0) }).map((_, index) => problems[index] || {});

  return (
    <>
      {rows.map((row, index) => (
        <tr key={`${shift}-${index}`}>
          {index === 0 && <td rowSpan={rows.length + extraRows} className="shift-cell">{SHIFT_LABELS[shift]}</td>}
          {index === 0 && <td rowSpan={rows.length + extraRows} className="team-cell">TEAM :<br />{text(getMain(data, shift).team_operasi)}</td>}
          <td className="time-cell">{[row.waktu_mulai, row.waktu_selesai].filter(Boolean).join(" - ")}</td>
          <td className="equipment-cell">{row.equipment || ""}</td>
          <td className="problem-cell">{row.problem || ""}</td>
          <td className="counter-cell">{row.countermeasure || ""}</td>
          {index === 0 && <td rowSpan={rows.length} className="information-cell"><ShiftInformation data={data} shift={shift} /></td>}
        </tr>
      ))}
      {Array.from({ length: extraRows }).map((_, index) => (
        <tr key={`${shift}-extra-${index}`} className="extra-print-row">
          <td></td>
          <td></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      ))}
    </>
  );
}

function RightDetailTable({ data }) {
  const extraCastingRows = useMemo(() => getProductionExtraRows(data, "castings", 10), [data]);
  const extraPengirimanRows = useMemo(() => getProductionExtraRows(data, "pengirimans", 9), [data]);

  return (
    <table className="print-table right-table">
      <colgroup>
        <col className="print-col-shift" />
        <col className="print-col-team" />
        <col className="print-col-time" />
        <col className="print-col-equipment" />
        <col className="print-col-problem" />
        <col className="print-col-counter" />
        <col className="print-col-information" />
      </colgroup>
      <thead>
        <tr>
          <th>Shift</th>
          <th></th>
          <th>Time</th>
          <th>Equipment</th>
          <th>Problem</th>
          <th>Countermeasure</th>
          <th>Information</th>
        </tr>
      </thead>
      <tbody>
        <ProblemRows data={data} shift="1" baseRows={11} extraRows={extraCastingRows} />
        <ProblemRows data={data} shift="2" baseRows={20} extraRows={extraPengirimanRows} />
        <ProblemRows data={data} shift="3" baseRows={11} />
      </tbody>
    </table>
  );
}

export default function PrintDailyReport() {
  const { tanggal } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/reports/print-daily", { params: { tanggal } });
      setData(res);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memuat data print");
    } finally {
      setLoading(false);
    }
  }, [tanggal]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="print-loading">Memuat format print...</div>;
  }

  if (!data) {
    return (
      <div className="print-loading">
        <p>Data print tidak tersedia.</p>
        <Button onClick={() => navigate("/data")}>Kembali</Button>
      </div>
    );
  }

  return (
    <div className="print-page-wrap">
      <style>{printStyles}</style>
      <div className="print-toolbar no-print">
        <Button variant="outline" onClick={() => navigate("/data")} className="rounded-sm">
          <ArrowLeft className="w-4 h-4 mr-1" /> Kembali
        </Button>
        <div className="toolbar-title">
          <strong>Print Laporan Harian Rodding</strong>
          <span>{formatDateId(data.tanggal)}</span>
        </div>
        <div className="toolbar-actions">
          <Button variant="outline" onClick={load} className="rounded-sm">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={() => window.print()} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
            <Printer className="w-4 h-4 mr-1" /> Cetak
          </Button>
        </div>
      </div>

      <div className="print-sheet">
        <header className="doc-header">
          <div className="brand-box">
            <img src="/inalum-logo.png" alt="Inalum" />
            <div>PT INDONESIA ASAHAN ALUMINIUM (Persero)</div>
          </div>
          <div className="doc-number">
            <div>No. Dokumen / Revisi</div>
            <strong>{data.document_no}</strong>
          </div>
        </header>

        <section className="title-row">
          <h1>LAPORAN HARIAN RODDING</h1>
          <table className="sign-table">
            <tbody>
              <tr>
                <td colSpan={3}>Dibuat oleh SI</td>
                <td>Diketahui</td>
              </tr>
              <tr>
                <td>Shift I</td>
                <td>Shift II</td>
                <td>Shift III</td>
                <td>M</td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </section>

        <div className="date-line">
          <strong>DAY / DATE :</strong>
          <span>{formatDateId(data.tanggal)}</span>
        </div>

        <main className="report-grid">
          <LeftSummaryTable data={data} />
          <RightDetailTable data={data} />
        </main>
      </div>
    </div>
  );
}

const printStyles = `
  @page {
    size: A4 landscape;
    margin: 4mm;
  }

  .print-page-wrap {
    min-height: 100vh;
    background: #e5e7eb;
    color: #000;
    padding: 18px;
    font-family: Arial, Helvetica, sans-serif;
  }

  .print-toolbar {
    max-width: 1500px;
    margin: 0 auto 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 10px;
  }

  .toolbar-title {
    display: flex;
    flex-direction: column;
    text-align: center;
    font-size: 13px;
  }

  .toolbar-title span {
    color: #64748b;
    font-size: 12px;
  }

  .toolbar-actions {
    display: flex;
    gap: 8px;
  }

  .print-loading {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 12px;
    color: #334155;
    background: #f8fafc;
  }

  .print-sheet {
    width: 1500px;
    margin: 0 auto;
    background: #fff;
    padding: 18px 24px 16px;
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
    transform-origin: top center;
  }

  .doc-header {
    width: 970px;
    margin: 0 auto 28px;
    display: grid;
    grid-template-columns: 1fr 270px;
    border: 2px solid #000;
  }

  .brand-box {
    height: 58px;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 14px;
    border-right: 2px solid #000;
    font-size: 23px;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  .brand-box img {
    width: 160px;
    height: 44px;
    object-fit: contain;
  }

  .doc-number {
    display: grid;
    grid-template-rows: 1fr 1fr;
    font-size: 16px;
  }

  .doc-number div,
  .doc-number strong {
    display: flex;
    align-items: center;
    padding: 0 10px;
  }

  .doc-number div {
    border-bottom: 2px solid #000;
  }

  .title-row {
    display: grid;
    grid-template-columns: 1fr 330px;
    align-items: start;
    gap: 20px;
    margin-bottom: 18px;
  }

  .title-row h1 {
    margin: 18px 0 0 225px;
    font-size: 44px;
    font-weight: 900;
    letter-spacing: -0.02em;
  }

  .sign-table {
    border-collapse: collapse;
    width: 330px;
    table-layout: fixed;
    font-size: 11px;
    text-align: center;
  }

  .sign-table td {
    border: 1.5px solid #000;
    height: 15px;
    padding: 0 3px;
  }

  .sign-table tr:nth-child(3) td {
    height: 36px;
  }

  .date-line {
    display: flex;
    gap: 14px;
    align-items: center;
    margin: 0 0 10px 2px;
    font-size: 13px;
  }

  .report-grid {
    display: grid;
    grid-template-columns: 462px 1fr;
    gap: 0;
    align-items: stretch;
  }

  .print-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 11px;
    line-height: 1.05;
  }

  .print-table th,
  .print-table td {
    border: 1.4px solid #000;
    padding: 1px 3px;
    vertical-align: middle;
    height: 17px;
  }

  .print-table th {
    font-weight: 800;
    text-align: center;
    font-size: 13px;
    height: 21px;
  }

  .left-table {
    border-right-width: 2px;
  }

  .left-table .item-col {
    width: 44px;
  }

  .left-table .lot-cell {
    text-align: left;
    width: 120px;
  }

  .vertical-label {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    text-align: center;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .num-cell {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  .total-row td,
  .section-row td {
    font-weight: 800;
    text-align: center;
  }

  .left-label {
    font-weight: 700;
  }

  .left-sub-label {
    font-weight: 500;
  }

  .center {
    text-align: center;
  }

  .right-table {
    border-left-width: 0;
  }

  .right-table .print-col-shift { width: 46px; }
  .right-table .print-col-team { width: 48px; }
  .right-table .print-col-time { width: 60px; }
  .right-table .print-col-equipment { width: 100px; }
  .right-table .print-col-problem { width: 370px; }
  .right-table .print-col-counter { width: 290px; }
  .right-table .print-col-information { width: 280px; }

  .shift-cell,
  .team-cell {
    text-align: center;
    font-weight: 800;
    vertical-align: top;
  }

  .team-cell {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    letter-spacing: 0.04em;
  }

  .time-cell,
  .equipment-cell {
    text-align: center;
  }

  .problem-cell,
  .counter-cell {
    text-align: left;
  }

  .information-cell {
    padding: 0 !important;
    vertical-align: top !important;
  }

  .info-box {
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  .info-line {
    min-height: 16px;
    display: grid;
    grid-template-columns: minmax(92px, 1fr) 56px 28px minmax(72px, 0.8fr) 46px 24px;
    align-items: center;
    border-bottom: 1px solid #000;
    padding: 0 3px;
    column-gap: 2px;
  }

  .info-line:last-child {
    border-bottom: 0;
  }

  .info-line strong {
    text-align: right;
    font-weight: 600;
    min-height: 12px;
  }

  .info-line em {
    text-align: right;
    font-style: normal;
  }

  .info-subtitle {
    min-height: 17px;
    display: flex;
    align-items: center;
    padding: 0 3px;
    border-bottom: 1px solid #000;
    font-weight: 800;
  }

  @media screen and (max-width: 1540px) {
    .print-sheet {
      transform: scale(0.82);
      margin-bottom: -160px;
    }
  }

  @media print {
    .no-print {
      display: none !important;
    }

    html,
    body,
    #root {
      width: 100%;
      min-height: 100%;
      background: #fff !important;
    }

    .print-page-wrap {
      background: #fff !important;
      padding: 0;
      width: 289mm;
    }

    .print-sheet {
      width: 289mm;
      min-height: auto;
      margin: 0;
      padding: 0;
      box-shadow: none;
      transform: none !important;
    }

    .doc-header {
      width: 192mm;
      margin: 0 auto 4mm;
      grid-template-columns: 1fr 52mm;
    }

    .brand-box {
      height: 14mm;
      gap: 3mm;
      padding: 0 3mm;
      font-size: 10pt;
    }

    .brand-box img {
      width: 30mm;
      height: 11mm;
    }

    .doc-number {
      font-size: 7pt;
    }

    .doc-number div,
    .doc-number strong {
      padding: 0 2mm;
    }

    .title-row {
      grid-template-columns: 1fr 52mm;
      gap: 4mm;
      margin-bottom: 3mm;
    }

    .title-row h1 {
      margin: 5mm 0 0 33mm;
      font-size: 22pt;
    }

    .sign-table {
      width: 52mm;
      font-size: 5.5pt;
    }

    .sign-table td {
      height: 3mm;
      padding: 0 1mm;
    }

    .sign-table tr:nth-child(3) td {
      height: 8mm;
    }

    .date-line {
      gap: 3mm;
      margin: 0 0 2mm;
      font-size: 6pt;
    }

    .report-grid {
      width: 289mm;
      grid-template-columns: 78mm 211mm;
      min-width: 0;
    }

    .left-table,
    .right-table {
      width: 100% !important;
      min-width: 0;
      max-width: 100%;
    }

    .print-table {
      font-size: 5.5pt;
      line-height: 1;
    }

    .print-table th,
    .print-table td {
      border-width: 0.3mm;
      padding: 0.2mm 0.4mm;
      height: 2.35mm;
    }

    .print-table th {
      font-size: 6pt;
      height: 3.2mm;
    }

    .right-table .print-col-shift { width: 8mm !important; }
    .right-table .print-col-team { width: 8mm !important; }
    .right-table .print-col-time { width: 11mm !important; }
    .right-table .print-col-equipment { width: 17mm !important; }
    .right-table .print-col-problem { width: 65mm !important; }
    .right-table .print-col-counter { width: 51mm !important; }
    .right-table .print-col-information { width: 51mm !important; }

    .problem-cell,
    .counter-cell,
    .information-cell {
      overflow-wrap: anywhere;
    }

    .info-line {
      min-height: 2.35mm;
      grid-template-columns: minmax(0, 1fr) 8mm 4mm minmax(0, 0.75fr) 7mm 3mm;
      padding: 0 0.5mm;
      column-gap: 0.4mm;
    }

    .info-subtitle {
      min-height: 2.35mm;
      padding: 0 0.5mm;
    }
  }
`;
